import { Factory } from 'fishery';

import { oneDayInMilliseconds } from '../data-contract/one-day-in-milliseconds';
import type {
    BaseSkill,
    HardSoftSkill,
    HardSoftSkillProficiencyWithUnknownLevel,
    HardSoftSkillShort,
    HardSoftSkillWithIconColor,
    JobArchitectureSkill,
    JobProfileSkill,
    LanguageSkill,
    LanguageSkillProficiencyWithUnknownLevel,
    LanguageSkillShort,
    LanguageSkillWithIconColor,
    PaginatedSkillCatalog,
    Skill,
    SkillCatalogOptions,
    SkillCatalogStatistics,
    SkillPreset,
    SkillReferences,
    SkillShort,
    SkillsOptions,
} from '../data-contract/skill';
import {
    hardSoftSkillProficiencyWithUnknownLevelSchema,
    languageSkillProficiencyWithUnknownLevelSchema,
} from '../data-contract/skill';
import type { RelatedJob } from '../data-contract/skill-details';
import { createUuidFromString } from '../uuid/uuid';
import { FactoryAutoRewindSequence } from './factory-auto-rewind-sequence';
import { paginationFactory } from './pagination.factories';

export const hardSoftSkillProficiencyLevelFactory =
    FactoryAutoRewindSequence.define<HardSoftSkillProficiencyWithUnknownLevel>(({ sequence }) =>
        hardSoftSkillProficiencyWithUnknownLevelSchema.parse(sequence % 5),
    );

export const languageSkillProficiencyLevelFactory =
    FactoryAutoRewindSequence.define<LanguageSkillProficiencyWithUnknownLevel>(({ sequence }) =>
        languageSkillProficiencyWithUnknownLevelSchema.parse(sequence % 6),
    );

export const skillShortFactory = FactoryAutoRewindSequence.define<SkillShort>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-short-skill-id-${sequence}`),
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const hardSoftSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`hard-soft-skill-id-${sequence}`),
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const hardSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`hard-skill-id-${sequence}`),
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: 'hard',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const softSkillShortFactory = FactoryAutoRewindSequence.define<HardSoftSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`soft-skill-id-${sequence}`),
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: 'soft',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const languageSkillShortFactory = FactoryAutoRewindSequence.define<LanguageSkillShort>(({ sequence }) => {
    const title =
        sequence % 3 !== 0
            ? `Language Skill ${sequence}`
            : `Really Really Really Really long Language Skill ${sequence}`;

    return {
        id: createUuidFromString(`language-skill-id-${sequence}`),
        isSynced: true,
        level: languageSkillProficiencyLevelFactory.build(),
        skillType: 'language',
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const skillFactory = FactoryAutoRewindSequence.define<Skill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        description: `Description ${sequence}`,
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const jobProfileSkillFactory = FactoryAutoRewindSequence.define<JobProfileSkill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        competencyId: `competency-id-${sequence % 3}`,
        description: `Description ${sequence}`,
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

const createLanguage = (sequence: number): LanguageSkill => {
    const title =
        sequence % 3 !== 0
            ? `Language Skill ${sequence}`
            : `Really Really Really Really long Language Skill ${sequence}`;

    return {
        id: createUuidFromString(`language-skill-id-${sequence}`),
        isSynced: true,
        level: languageSkillProficiencyLevelFactory.build(),
        skillType: 'language',
        title,
        titles: [{ language: 'en-US', title }],
    };
};

export const languageSkillFactory = FactoryAutoRewindSequence.define<LanguageSkill>(({ sequence }) =>
    createLanguage(sequence),
);

export const languageSkillWithIconColorFactory = FactoryAutoRewindSequence.define<LanguageSkillWithIconColor>(
    ({ sequence }) => {
        return {
            ...createLanguage(sequence),
            iconColor: '#ffffff',
        };
    },
);

const createHardSoftSkill = (sequence: number): HardSoftSkill => {
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        isSynced: true,
        level: hardSoftSkillProficiencyLevelFactory.build(),
        skillType: sequence % 2 !== 0 ? 'hard' : 'soft',
        title,
        titles: [{ language: 'en-US', title }],
    };
};

export const hardSoftSkillFactory = FactoryAutoRewindSequence.define<HardSoftSkill>(({ sequence }) => {
    return createHardSoftSkill(sequence);
});

export const hardSoftSkillWithIconColorFactory = FactoryAutoRewindSequence.define<HardSoftSkillWithIconColor>(
    ({ sequence }) => {
        return {
            ...createHardSoftSkill(sequence),
            iconColor: '#ffffff',
        };
    },
);

export const skillBasicFactory = FactoryAutoRewindSequence.define<BaseSkill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0
            ? `Related Skill ${sequence}`
            : `Really Really Really Really Really long Related Skill ${sequence}`;

    return {
        id: createUuidFromString(`skill-id-${sequence}`),
        isSynced: true,
        skillType,
        title,
        titles: [{ language: 'en-US', title }],
    };
});

export const relatedJobFactory = FactoryAutoRewindSequence.define<RelatedJob, { readonly currentTimestamp: number }>(
    ({ sequence, transientParams: { currentTimestamp = Date.now() } }) => {
        const title =
            sequence % 3 !== 0
                ? `Related Job Skill ${sequence}`
                : `Really Really Really Really Really long Related Job Skill ${sequence}`;

        return {
            id: createUuidFromString(`related-job-skill-id-${sequence}`),
            publishedAt: new Date(currentTimestamp - sequence * oneDayInMilliseconds).toISOString(),
            skills: skillFactory.buildList((sequence % 5) + 1),
            title,
        };
    },
);

export const skillPresetFactory = FactoryAutoRewindSequence.define<SkillPreset>(({ sequence }) => ({
    hardSoftSkills: hardSoftSkillShortFactory.buildList(8),
    languages: languageSkillShortFactory.buildList(2),
    name: `Predefined Profile ${sequence}`,
}));

export const skillsOptionsFactory = Factory.define<SkillsOptions>(() => ({
    canDetectSkills: true,
    canReadRelatedSkills: true,
}));

export const skillCatalogOptionsFactory = Factory.define<SkillCatalogOptions>(() => ({
    canManageSkillCatalog: true,
    canReadSkillCatalog: true,
}));

export const jobArchitectureSkillFactory = FactoryAutoRewindSequence.define<JobArchitectureSkill>(({ sequence }) => {
    const skillType = sequence % 2 !== 0 ? 'hard' : 'soft';
    const title =
        sequence % 3 !== 0 ? `Skill ${sequence}` : `Really Really Really Really Really long Skill ${sequence}`;
    const architectureId = createUuidFromString(`job-architecture-id-${sequence}`);

    const rolesItemCount = Math.min(sequence + 1, 20);
    const rolesTotalCount = sequence % 4 === 0 ? 247 : rolesItemCount;
    const levelsItemCount = Math.min(sequence + 2, 20);
    const levelsTotalCount = sequence % 4 === 0 ? 1203 : levelsItemCount;
    const competenciesItemCount = sequence % 3 === 0 ? 0 : Math.min(sequence, 20);
    const competenciesTotalCount = competenciesItemCount === 0 ? 0 : sequence % 4 === 0 ? 57 : competenciesItemCount;
    const tasksItemCount = sequence % 5 === 0 ? 0 : Math.min(sequence, 8);
    const tasksTotalCount = tasksItemCount === 0 ? 0 : sequence % 4 === 0 ? 19 : tasksItemCount;

    return {
        competencies: {
            items: Array.from({ length: competenciesItemCount }, (_, index) => ({
                id: createUuidFromString(`competency-reference-id-${sequence}-${index}`),
                title: `Competency ${sequence}-${index}`,
            })),
            totalCount: competenciesTotalCount,
        },
        id: createUuidFromString(`job-architecture-skill-id-${sequence}`),
        isSynced: true,
        jobArchitecturesWithSkill: [
            {
                id: architectureId,
                title: `Architecture ${sequence}`,
            },
            {
                id: createUuidFromString(`job-architecture-id-${sequence + 1}`),
                title: `Architecture ${sequence + 1}`,
            },
        ],
        jobProfilesWithThisSkill: sequence * 3,
        jobsWithThisSkill: sequence * 4,
        learningOpportunitiesWithSkill: [
            {
                id: createUuidFromString(`course-id-${sequence}`),
                title: `Course ${sequence}`,
            },
            {
                id: createUuidFromString(`course-id-${sequence + 1}`),
                title: `Course ${sequence + 1}`,
            },
        ],
        learningOpportunitiesWithThisSkill: sequence * 2,
        levels: {
            items: Array.from({ length: levelsItemCount }, (_, index) => ({
                architectureId,
                id: createUuidFromString(`job-level-id-${sequence}-${index}`),
                isFromPublishedVersion: index % 3 !== 0,
                layer: 'level',
                title: `Level ${sequence}-${index}`,
            })),
            totalCount: levelsTotalCount,
        },
        roles: {
            items: Array.from({ length: rolesItemCount }, (_, index) => ({
                architectureId,
                id: createUuidFromString(`job-role-id-${sequence}-${index}`),
                isFromPublishedVersion: index % 2 === 0,
                layer: 'role',
                title: `Role ${sequence}-${index}`,
            })),
            totalCount: rolesTotalCount,
        },
        skillType,
        syncStatus: 'NOT_SYNCED',
        tasks: {
            items: Array.from({ length: tasksItemCount }, (_, index) => ({
                architectureId,
                id: createUuidFromString(`skill-catalog-task-id-${sequence}-${index}`),
                itemId: createUuidFromString(`skill-catalog-task-item-id-${sequence}-${index}`),
                itemTitle: index % 2 === 0 ? `Role ${sequence}-${index}` : `Level ${sequence}-${index}`,
                layer: index % 2 === 0 ? 'role' : 'level',
                title: `Task ${sequence}-${index}`,
                version: 1,
            })),
            totalCount: tasksTotalCount,
        },
        title,
        titles: [{ language: 'en-US', title }],
        usersWithThisSkill: sequence * 5,
    };
});

export const skillCatalogStatisticsFactory = Factory.define<SkillCatalogStatistics>(() => ({
    deactivatedSkills: 5,
    skillsToReview: 12,
    syncedSkills: 80,
    totalSkills: 150,
}));

export const paginatedSkillCatalogFactory = FactoryAutoRewindSequence.define<PaginatedSkillCatalog>(() => ({
    data: jobArchitectureSkillFactory.buildList(8),
    pagination: paginationFactory.build(),
}));

export const skillReferencesFactory = FactoryAutoRewindSequence.define<SkillReferences>(({ sequence }) => {
    const architectureId = createUuidFromString(`skill-references-architecture-${sequence}`);
    const build = <T>(count: number, item: (index: number) => T): readonly T[] =>
        Array.from({ length: count }, (_, index) => item(index));

    return {
        competencies: {
            items: build(2, (index) => ({
                id: createUuidFromString(`skill-references-competency-${sequence}-${index}`),
                title: `Competency ${sequence}-${index}`,
            })),
            totalCount: 2,
        },
        employees: { totalCount: 12 + sequence },
        jobPostings: {
            items: build(2, (index) => ({
                id: createUuidFromString(`skill-references-job-${sequence}-${index}`),
                publishedAt: index === 0 ? new Date(Date.UTC(2026, 0, 15 + sequence)).toISOString() : null,
                title: `Job posting ${sequence}-${index}`,
            })),
            totalCount: 2,
        },
        learningOpportunities: {
            items: build(1, (index) => ({
                id: createUuidFromString(`skill-references-course-${sequence}-${index}`),
                title: `Course ${sequence}-${index}`,
            })),
            totalCount: 1,
        },
        levels: {
            items: build(3, (index) => ({
                architectureId,
                id: createUuidFromString(`skill-references-level-${sequence}-${index}`),
                isFromPublishedVersion: true,
                layer: 'level',
                title: `Level ${sequence}-${index}`,
            })),
            totalCount: 3,
        },
        roles: {
            items: build(2, (index) => ({
                architectureId,
                id: createUuidFromString(`skill-references-role-${sequence}-${index}`),
                isFromPublishedVersion: true,
                layer: 'role',
                title: `Role ${sequence}-${index}`,
            })),
            totalCount: 2,
        },
        tasks: {
            items: build(2, (index) => ({
                architectureId,
                id: createUuidFromString(`skill-references-task-${sequence}-${index}`),
                itemId: createUuidFromString(`skill-references-role-${sequence}-${index}`),
                itemTitle: `Role ${sequence}-${index}`,
                layer: 'role',
                title: `Task ${sequence}-${index}`,
                version: 1,
            })),
            totalCount: 2,
        },
    };
});
